import { authenticatedBlobFetch, authenticatedFetch } from "./http";
import type { CompatibilityResult, PackagingDefinition, ProductAuditEntry, ProductItem, ProductPackaging, Supplier } from "./models";

export interface MaterialStockPolicy {
  id: string;
  itemId: string;
  itemCode: string;
  itemDisplayName: string;
  baseUom: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseDisplayName: string;
  clientId: string | null;
  clientCode: string | null;
  clientDisplayName: string | null;
  safetyStockQuantity: number;
  minimumQuantity: number;
  maximumQuantity: number;
  replenishmentMultiple: number;
  minimumReplenishmentQuantity: number | null;
  leadTimeDays: number;
  executionMode: "DIRECT_SUPPLIER_ORDER" | "PURCHASE_REQUEST";
  outputProfileId: string | null;
  outputProfileCode: string | null;
  outputProfileDisplayName: string | null;
  status: "ACTIVE" | "INACTIVE";
  version: number;
}

export interface MaterialStockPolicyMutation {
  warehouseId?: string;
  safetyStockQuantity: number;
  minimumQuantity: number;
  maximumQuantity: number;
  replenishmentMultiple: number;
  minimumReplenishmentQuantity: number | null;
  leadTimeDays: number;
  executionMode: "DIRECT_SUPPLIER_ORDER" | "PURCHASE_REQUEST";
  outputProfileId: string | null;
  status?: "ACTIVE" | "INACTIVE";
  version?: number;
}

export interface ReplenishmentOutputProfile {
  id: string;
  profileCode: string;
  displayName: string;
  description: string | null;
  successRule: "ALL_REQUIRED" | "ANY_CHANNEL" | "MANUAL_VALIDATION";
  status: "ACTIVE" | "INACTIVE";
  channels: ReplenishmentOutputChannel[];
}

export interface ReplenishmentOutputChannel {
  id: string;
  channelType: "API" | "PDF" | "EMAIL" | "WEBHOOK" | "FILE_EXPORT" | "MANUAL";
  sequenceNumber: number;
  required: boolean;
  configuration: Record<string, unknown>;
  status: "ACTIVE" | "INACTIVE";
}

export interface ReplenishmentDocumentTemplateOption {
  id: string; templateNumber: string; displayName: string;
  languageCode: string; outputFormat: string;
}

export function loadReplenishmentOutputProfiles(): Promise<ReplenishmentOutputProfile[]> {
  return authenticatedFetch<ReplenishmentOutputProfile[]>(
    "/api/v1/replenishment-configuration/output-profiles"
  );
}

export function loadReplenishmentDocumentTemplates(): Promise<ReplenishmentDocumentTemplateOption[]> {
  return authenticatedFetch("/api/v1/replenishment-configuration/document-templates");
}

export type ReplenishmentOutputProfileMutation = Omit<ReplenishmentOutputProfile, "id" | "channels">;
export type ReplenishmentOutputChannelMutation = Omit<ReplenishmentOutputChannel, "id">;

export function saveReplenishmentOutputProfile(id: string | null, body: ReplenishmentOutputProfileMutation) {
  return authenticatedFetch<ReplenishmentOutputProfile>(
    `/api/v1/replenishment-configuration/output-profiles${id ? `/${id}` : ""}`,
    { method: id ? "PUT" : "POST", body: JSON.stringify(body) }
  );
}

export function saveReplenishmentOutputChannel(profileId: string, id: string | null,
  body: ReplenishmentOutputChannelMutation) {
  return authenticatedFetch<ReplenishmentOutputChannel>(
    `/api/v1/replenishment-configuration/output-profiles/${profileId}/channels${id ? `/${id}` : ""}`,
    { method: id ? "PUT" : "POST", body: JSON.stringify(body) }
  );
}

export interface MaterialStockPolicyEvaluation {
  policy: MaterialStockPolicy;
  onHandQuantity: number;
  unavailableQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  confirmedInboundQuantity: number;
  nextConfirmedInboundAt: string | null;
  projectedAvailableQuantity: number;
  averageDailyConsumption: number;
  forecastAvailableQuantity: number;
  coverageDays: number | null;
  projectedStockoutDate: string | null;
  recommendedLaunchDate: string | null;
  recommendedReplenishmentQuantity: number;
  evaluationStatus: "HEALTHY" | "WATCH" | "REPLENISH" | "CRITICAL" | "INACTIVE";
  proposalId: string | null;
  proposalStatus: ReplenishmentProposal["status"] | null;
}

export interface ReplenishmentProposal {
  id: string; policyId: string; itemId: string; itemCode: string;
  itemDisplayName: string; warehouseId: string; warehouseCode: string;
  clientId: string | null; clientCode: string | null;
  status: "PROPOSED" | "UNDER_REVIEW" | "APPROVED" | "REJECTED" | "ORDERED";
  baseUom: string; safetyStockQuantity: number; minimumQuantity: number;
  maximumQuantity: number; onHandQuantity: number; unavailableQuantity: number;
  reservedQuantity: number; availableQuantity: number;
  confirmedInboundQuantity: number; nextConfirmedInboundAt: string | null;
  averageDailyConsumption: number; forecastAvailableQuantity: number;
  projectedStockoutDate: string | null; recommendedLaunchDate: string | null;
  recommendedQuantity: number; evaluationStatus: MaterialStockPolicyEvaluation["evaluationStatus"];
  decidedQuantity: number | null; decisionReason: string | null;
  createdAt: string; reviewedAt: string | null; decidedAt: string | null; version: number;
}

export function loadMaterialStockPolicies(itemId: string): Promise<MaterialStockPolicy[]> {
  return authenticatedFetch<MaterialStockPolicy[]>(`/api/v1/products/${itemId}/stock-policies`);
}

export function loadMaterialStockPolicyEvaluations(
  itemId: string
): Promise<MaterialStockPolicyEvaluation[]> {
  return authenticatedFetch<MaterialStockPolicyEvaluation[]>(
    `/api/v1/products/${itemId}/stock-policies/evaluations`
  );
}

export function loadStockRisk(): Promise<MaterialStockPolicyEvaluation[]> {
  return authenticatedFetch<MaterialStockPolicyEvaluation[]>("/api/v1/products/stock-risk");
}

export interface StockRiskProjectionPoint {
  date: string;
  openingQuantity: number;
  firmDemandQuantity: number;
  forecastConsumptionQuantity: number;
  expiryQuantity: number;
  inboundQuantity: number;
  closingQuantity: number;
}

export interface StockRiskCause {
  type: "FIRM_DEMAND" | "FORECAST_CONSUMPTION" | "EXPIRY" | "OPENING_SHORTAGE";
  date: string;
  quantity: number;
  reference: string | null;
}

export interface StockRiskEvent {
  type: "FIRM_DEMAND" | "FORECAST_CONSUMPTION" | "EXPIRY" | "CONFIRMED_SUPPLY";
  date: string;
  quantity: number;
  reference: string | null;
  closingQuantity: number;
}

export interface StockRiskV2Evaluation {
  policy: MaterialStockPolicy;
  onHandQuantity: number;
  unavailableQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  firmDemandQuantity: number;
  confirmedInboundQuantity: number;
  nextConfirmedInboundAt: string | null;
  expiringQuantity: number;
  averageDailyConsumption: number;
  projectedEndQuantity: number;
  reorderPointDate: string | null;
  safetyStockDate: string | null;
  projectedStockoutDate: string | null;
  primaryStockoutCause: StockRiskCause | null;
  recoveryDate: string | null;
  recommendedLaunchDate: string | null;
  maximumDeficitQuantity: number;
  supplyGapDays: number | null;
  recommendedActionQuantity: number;
  riskStatus: "CRITICAL" | "COVERED_LATE" | "REPLENISH" | "WATCH" | "HEALTHY" | "INACTIVE";
  actionType: "NONE" | "EXPEDITE" | "TRACK_EXISTING" | "LAUNCH_REPLENISHMENT";
  coveredByExistingSupply: boolean;
  supplyReference: string | null;
  supplierDisplayName: string | null;
  proposalId: string | null;
  proposalStatus: ReplenishmentProposal["status"] | null;
  events: StockRiskEvent[];
  projection: StockRiskProjectionPoint[];
}

export function loadStockRiskV2(
  horizonDays: number, includeForecast: boolean
): Promise<StockRiskV2Evaluation[]> {
  return authenticatedFetch<StockRiskV2Evaluation[]>(
    `/api/v1/products/stock-risk-v2?horizonDays=${horizonDays}&includeForecast=${includeForecast}`
  );
}

export function startReplenishmentProposalReview(
  policyId: string
): Promise<ReplenishmentProposal> {
  return authenticatedFetch<ReplenishmentProposal>(
    `/api/v1/replenishment-proposals/policies/${policyId}/review`, { method: "POST" }
  );
}

export function loadReplenishmentProposal(
  proposalId: string
): Promise<ReplenishmentProposal> {
  return authenticatedFetch<ReplenishmentProposal>(
    `/api/v1/replenishment-proposals/${proposalId}`
  );
}

export function decideReplenishmentProposal(
  proposalId: string,
  request: { quantity: number; decision: "APPROVED" | "REJECTED"; reason: string; version: number }
): Promise<ReplenishmentProposal> {
  return authenticatedFetch<ReplenishmentProposal>(
    `/api/v1/replenishment-proposals/${proposalId}/decision`, {
      method: "PATCH", body: JSON.stringify(request)
    }
  );
}

export interface ReplenishmentExecution {
  id: string; proposalId: string; itemId: string; itemCode: string;
  itemDisplayName: string; warehouseCode: string;
  executionMode: "DIRECT_SUPPLIER_ORDER" | "PURCHASE_REQUEST";
  outputProfileDisplayName: string | null; approvedQuantity: number; baseUom: string;
  status: "APPROVED" | "PURCHASE_REQUEST_ISSUED" | "ORDERED" | "ACKNOWLEDGED"
    | "ASN_CONFIRMED" | "PARTIALLY_RECEIVED" | "RECEIVED" | "CLOSED" | "CANCELLED";
  externalReference: string | null;
  purchaseRequestReference: string | null;
  purchaseOrderReference: string | null;
  primaryReference: string | null;
  expectedAt: string | null; overdue: boolean;
  acknowledgedAt: string | null; createdAt: string; updatedAt: string; version: number;
  documentId: string | null; documentStatus: "PENDING" | "RUNNING" | "SUCCEEDED"
    | "FAILED" | "RETRY" | "CANCELLED" | null;
  receivedQuantity: number;
  putawayQuantity: number;
}

export function loadReplenishmentDocument(id: string): Promise<Blob> {
  return authenticatedBlobFetch(`/api/v1/replenishment-executions/${id}/document`);
}

export function loadReplenishmentExecutions(): Promise<ReplenishmentExecution[]> {
  return authenticatedFetch<ReplenishmentExecution[]>("/api/v1/replenishment-executions");
}

export function issueReplenishmentExecution(id: string, request: {
  externalReference: string | null; expectedAt: string | null; version: number;
}): Promise<ReplenishmentExecution> {
  return authenticatedFetch<ReplenishmentExecution>(
    `/api/v1/replenishment-executions/${id}/issue`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function recordReplenishmentPurchaseOrder(id: string, request: {
  purchaseOrderReference: string; expectedAt: string | null; version: number;
}): Promise<ReplenishmentExecution> {
  return authenticatedFetch<ReplenishmentExecution>(
    `/api/v1/replenishment-executions/${id}/purchase-order`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function createMaterialStockPolicy(
  itemId: string, request: MaterialStockPolicyMutation
): Promise<MaterialStockPolicy> {
  return authenticatedFetch<MaterialStockPolicy>(`/api/v1/products/${itemId}/stock-policies`, {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateMaterialStockPolicy(
  itemId: string, policyId: string, request: MaterialStockPolicyMutation
): Promise<MaterialStockPolicy> {
  return authenticatedFetch<MaterialStockPolicy>(
    `/api/v1/products/${itemId}/stock-policies/${policyId}`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function loadProducts(): Promise<ProductItem[]> {
  return authenticatedFetch<ProductItem[]>("/api/v1/products");
}

export function loadProduct(itemId: string): Promise<ProductItem> {
  return authenticatedFetch<ProductItem>(`/api/v1/products/${itemId}`);
}

export function createProduct(request: Record<string, unknown>): Promise<ProductItem> {
  return authenticatedFetch<ProductItem>("/api/v1/products", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateProduct(
  itemId: string, displayName: string, abcClass: "A" | "B" | "C" | null,
  supplierIds: string[], primarySupplierId: string, version: number
): Promise<ProductItem> {
  return authenticatedFetch<ProductItem>(`/api/v1/products/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify({ displayName, abcClass, supplierIds, primarySupplierId, version })
  });
}

export function updateProductRotationPolicy(
  itemId: string,
  request: {
    rotationPolicy: "FIFO" | "FEFO" | "LIFO" | "NONE";
    minimumRemainingShelfLifeDays: number | null;
    lotMixingAllowed: boolean;
    partialHuAllowed: boolean;
    reason: string;
    version: number;
  }
): Promise<ProductItem> {
  return authenticatedFetch<ProductItem>(
    `/api/v1/products/${itemId}/rotation-policy`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function archiveProduct(
  itemId: string, version: number
): Promise<ProductItem> {
  return authenticatedFetch<ProductItem>(`/api/v1/products/${itemId}`, {
    method: "DELETE",
    body: JSON.stringify({ version })
  });
}

export function changeProductStatus(
  itemId: string, status: "DRAFT", version: number
): Promise<ProductItem> {
  return authenticatedFetch<ProductItem>(`/api/v1/products/${itemId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, version })
  });
}

export function loadPackaging(itemId: string): Promise<ProductPackaging[]> {
  return authenticatedFetch<ProductPackaging[]>(`/api/v1/products/${itemId}/packaging`);
}

export function createPackaging(
  itemId: string, request: Record<string, unknown>
): Promise<ProductPackaging> {
  return authenticatedFetch<ProductPackaging>(`/api/v1/products/${itemId}/packaging`, {
    method: "POST", body: JSON.stringify(request)
  });
}

export function archiveProductPackaging(
  itemId: string, packagingId: string, version: number
): Promise<ProductPackaging> {
  return authenticatedFetch<ProductPackaging>(
    `/api/v1/products/${itemId}/packaging/${packagingId}`,
    {
      method: "DELETE",
      body: JSON.stringify({ version })
    }
  );
}

export function loadPackagingDefinitions(): Promise<PackagingDefinition[]> {
  return authenticatedFetch<PackagingDefinition[]>("/api/v1/packaging-definitions");
}

export function createPackagingDefinition(
  request: Record<string, unknown>
): Promise<PackagingDefinition> {
  return authenticatedFetch<PackagingDefinition>("/api/v1/packaging-definitions", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updatePackagingDefinition(
  definitionId: string, request: Record<string, unknown>
): Promise<PackagingDefinition> {
  return authenticatedFetch<PackagingDefinition>(
    `/api/v1/packaging-definitions/${definitionId}`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function archivePackagingDefinition(
  definitionId: string, version: number
): Promise<PackagingDefinition> {
  return authenticatedFetch<PackagingDefinition>(
    `/api/v1/packaging-definitions/${definitionId}`,
    { method: "DELETE", body: JSON.stringify({ version }) }
  );
}

export function loadProductAudit(itemId: string): Promise<ProductAuditEntry[]> {
  return authenticatedFetch<ProductAuditEntry[]>(`/api/v1/products/${itemId}/audit`);
}

export function checkCompatibility(
  packagingId: string, binId: string, quantity: number
): Promise<CompatibilityResult> {
  const params = new URLSearchParams({
    packagingId, binId, quantity: String(quantity)
  });
  return authenticatedFetch<CompatibilityResult>(
    `/api/v1/products/compatibility?${params}`
  );
}

export function loadSuppliers(): Promise<Supplier[]> {
  return authenticatedFetch<Supplier[]>("/api/v1/inbound/suppliers");
}

export function createSupplier(
  request: Record<string, unknown>
): Promise<Supplier> {
  return authenticatedFetch<Supplier>("/api/v1/inbound/suppliers", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateSupplier(
  supplierId: string, request: Record<string, unknown>
): Promise<Supplier> {
  return authenticatedFetch<Supplier>(
    `/api/v1/inbound/suppliers/${supplierId}`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function archiveSupplier(
  supplierId: string, version: number
): Promise<Supplier> {
  return authenticatedFetch<Supplier>(
    `/api/v1/inbound/suppliers/${supplierId}`,
    { method: "DELETE", body: JSON.stringify({ version }) }
  );
}
